// SCR-CONSOLE-008 — داده مرجع (US-PLT-090): مجموعه‌داده‌ها و نسخه‌ها، بارگذاری CSV یا JSON با پیش‌نمایش و خطاهای ردیف،
// تاریخ مؤثر، انتشار با تأیید (تغییرناپذیر — BR-PLT-100) و نشان IsSample.
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type ReferenceDatasetView,
  type ReferenceVersionView,
} from '@phoenix/api-client';
import { useIdempotencyKey, usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  CheckboxRHF,
  ConfirmDialog,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  QueryState,
  SampleBadge,
  StatusChip,
  TextFieldRHF,
  zBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { consoleKeys, toNumber } from '../../api';
import { detectFormat, previewReferenceFile, type ReferencePreview } from './preview';

export const SCR_CONSOLE_008 = 'SCR-CONSOLE-008';
export const SCR_CONSOLE_041 = 'SCR-CONSOLE-041';

/**
 * فهرست‌های مقرراتی (US-CFG-042، AC-CFG-042.1): نسخه‌دار با تاریخ مؤثر و **مرجع مقرراتی**. ماژول‌های مصرف‌کننده (TEL، IAM، MSG)
 * همیشه از همین منبع می‌خوانند، نه از ثابت در کد.
 */
export const REGULATORY_DATASETS = ['TELEMED_RESTRICTED_DRUGS', 'REQUIRED_PROVIDER_CREDENTIALS', 'MARKETING_BLOCKED_TERMS'];

const uploadSchema = z.object({
  effectiveFrom: zBusinessDate(),
  isSample: z.boolean(),
  // AP-DOC-03 (US-CFG-042): مرجع مقرراتی نسخه؛ برای فهرست‌های مقرراتی پر می‌شود و برای بقیه تهی می‌ماند.
  regulatoryReference: z.string().max(300).optional(),
});
type UploadForm = z.input<typeof uploadSchema>;

interface LoadedFile {
  name: string;
  content: string;
  preview: ReferencePreview;
}

/** خواندن متن فایل (UTF-8) با FileReader (سازگار با همه مرورگرها و jsdom). */
function readText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsText(file, 'utf-8');
  });
}

function rowErrors(error: unknown): string[] {
  if (isApiError(error) && Array.isArray(error.extensions['rows'])) {
    return (error.extensions['rows'] as unknown[]).filter((row): row is string => typeof row === 'string');
  }
  return [];
}

function UploadDialog({
  dataset,
  open,
  onClose,
}: {
  dataset: ReferenceDatasetView;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const fileInputId = useId();
  const [file, setFile] = useState<LoadedFile | null>(null);
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const { control, handleSubmit, formState, reset } = useForm<UploadForm>({
    resolver: zodResolver(uploadSchema),
    defaultValues: { effectiveFrom: undefined as unknown as string, isSample: false, regulatoryReference: '' },
  });

  const upload = useMutation({
    mutationFn: (values: UploadForm & { file: LoadedFile }) =>
      unwrap(
        api.POST('/api/v1/platform/console/reference-data/{dataset}/versions', {
          params: { path: { dataset: dataset.code } },
          body: {
            format: values.file.preview.format,
            content: values.file.content,
            effectiveFrom: values.effectiveFrom,
            sourceFile: values.file.name,
            isSample: values.isSample,
            regulatoryReference: values.regulatoryReference?.trim() ? values.regulatoryReference.trim() : null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consoleKeys.referenceData });
      close();
    },
  });

  function close() {
    reset();
    setFile(null);
    setFileProblem(null);
    upload.reset();
    onClose();
  }

  const onFile = async (selected: File | undefined) => {
    setFile(null);
    setFileProblem(null);
    if (!selected) {
      return;
    }
    const format = detectFormat(selected.name);
    if (!format) {
      setFileProblem('console:reference_data.upload.problem_format');
      return;
    }
    const content = await readText(selected);
    setFile({ name: selected.name, content, preview: previewReferenceFile(content, format) });
  };

  const submit = handleSubmit((values) => {
    if (!file) {
      setFileProblem('console:reference_data.upload.problem_no_file');
      return;
    }
    upload.mutate({ ...values, file });
  });

  const errors = rowErrors(upload.error);

  return (
    <FormDialog
      open={open}
      titleKey="console:reference_data.upload.title"
      onSubmit={() => void submit()}
      onClose={close}
      isDirty={formState.isDirty || file !== null}
      isSubmitting={upload.isPending}
      submitError={errors.length ? undefined : upload.error}
      submitKey="console:reference_data.upload.submit"
      maxWidth="md"
    >
      <Typography color="text.secondary">
        {t('console:reference_data.upload.dataset', { code: dataset.code })}
      </Typography>
      <Box>
        <Button
          component="label"
          htmlFor={fileInputId}
          variant="outlined"
          startIcon={<UploadFileOutlinedIcon />}
        >
          {t('console:reference_data.upload.choose_file')}
        </Button>
        <input
          id={fileInputId}
          type="file"
          accept=".csv,.json,text/csv,application/json"
          hidden
          aria-label={t('console:reference_data.upload.choose_file')}
          data-testid="reference-file-input"
          onChange={(event) => void onFile(event.target.files?.[0])}
        />
        {file && (
          <Typography variant="body2" component="span" sx={{ ms: 1 }}>
            <bdi dir="ltr">{file.name}</bdi>
          </Typography>
        )}
        {fileProblem && (
          <Typography color="error" variant="body2" role="alert">
            {t(fileProblem)}
          </Typography>
        )}
      </Box>
      {file && (
        <Box data-testid="reference-preview">
          <Typography variant="subtitle2">
            {t('console:reference_data.upload.preview', { total: faDigits(file.preview.total) })}
          </Typography>
          {file.preview.problemKey && (
            <Alert severity="warning" sx={{ my: 1 }}>
              {t(file.preview.problemKey)}
            </Alert>
          )}
          {file.preview.columns.length > 0 && (
            <Box sx={{ overflow: 'auto', maxHeight: 220 }}>
              <Table size="small" aria-label={t('console:reference_data.upload.preview_caption')}>
                <TableHead>
                  <TableRow>
                    {file.preview.columns.map((column) => (
                      <TableCell key={column}>
                        <bdi dir="ltr">{column}</bdi>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {file.preview.rows.map((row, index) => (
                    <TableRow key={index}>
                      {file.preview.columns.map((column) => (
                        <TableCell key={column}>{row[column]}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}
      <JalaliDatePickerRHF
        control={control}
        name="effectiveFrom"
        labelKey="console:reference_data.upload.effective_from"
        helperKey="console:reference_data.upload.effective_from_help"
        required
      />
      <CheckboxRHF
        control={control}
        name="isSample"
        labelKey="console:reference_data.upload.is_sample"
        helperKey="console:reference_data.upload.is_sample_help"
      />
      <TextFieldRHF
        control={control}
        name="regulatoryReference"
        labelKey="console:reference_data.upload.regulatory_reference"
        helperKey="console:reference_data.upload.regulatory_reference_help"
        slotProps={{ htmlInput: { 'data-testid': 'reference-regulatory-reference' } }}
      />
      {errors.length > 0 && (
        <Alert severity="error" data-testid="reference-row-errors" data-error-code="PHX-PLT-0502">
          <AlertTitle>{t('errors:error.reference_file_invalid')}</AlertTitle>
          <Box component="ul" dir="ltr" sx={{ m: 0, ps: 2 }}>
            {errors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </Box>
        </Alert>
      )}
    </FormDialog>
  );
}

function DatasetCard({
  dataset,
  onUpload,
  onPublish,
}: {
  dataset: ReferenceDatasetView;
  onUpload: () => void;
  onPublish: (version: ReferenceVersionView) => void;
}) {
  const { t } = useTranslation('console');
  const versions = [...dataset.versions].sort(
    (a, b) => (toNumber(b.version) ?? 0) - (toNumber(a.version) ?? 0),
  );
  const columns: DataTableColumn<ReferenceVersionView>[] = [
    {
      id: 'version',
      headerKey: 'console:reference_data.columns.version',
      cell: (row) => faDigits(toNumber(row.version) ?? 0),
    },
    {
      id: 'status',
      headerKey: 'console:reference_data.columns.status',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <StatusChip
            tone={row.status === 'Published' ? 'active' : 'pending'}
            labelKey={`console:reference_data.version_status.${row.status === 'Published' ? 'Published' : 'Draft'}`}
          />
          {row.isSample && <SampleBadge />}
        </Stack>
      ),
    },
    {
      id: 'effectiveFrom',
      headerKey: 'console:reference_data.columns.effective_from',
      cell: (row) => <JalaliDate value={row.effectiveFrom} />,
    },
    {
      id: 'items',
      headerKey: 'console:reference_data.columns.items',
      cell: (row) => faDigits(toNumber(row.itemCount) ?? 0),
    },
    {
      id: 'source',
      headerKey: 'console:reference_data.columns.source_file',
      cell: (row) => (row.sourceFile ? <bdi dir="ltr">{row.sourceFile}</bdi> : t('common:states.none')),
    },
    {
      id: 'regulatoryReference',
      headerKey: 'console:reference_data.columns.regulatory_reference',
      cell: (row) =>
        row.regulatoryReference ? (
          <span data-testid={`reference-regulatory-${dataset.code}-${toNumber(row.version) ?? 0}`}>{row.regulatoryReference}</span>
        ) : (
          t('common:states.none')
        ),
    },
    {
      id: 'publishedAt',
      headerKey: 'console:reference_data.columns.published_at',
      cell: (row) => (row.publishedAt ? <JalaliDate value={row.publishedAt} /> : t('common:states.none')),
    },
    {
      id: 'actions',
      headerKey: 'console:reference_data.columns.actions',
      cell: (row) =>
        row.status !== 'Published' ? (
          <Button
            size="small"
            variant="contained"
            onClick={() => onPublish(row)}
            data-testid={`reference-publish-${dataset.code}-${toNumber(row.version) ?? 0}`}
          >
            {t('console:reference_data.publish')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-dataset={dataset.code}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3" component="h2">
            {t(`console:reference_data.datasets.${dataset.code}`, { defaultValue: dataset.code })}{' '}
            <Typography component="span" color="text.secondary" variant="body2">
              <bdi dir="ltr">{dataset.code}</bdi>
            </Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('console:reference_data.consumers', { consumers: dataset.consumers })}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<UploadFileOutlinedIcon />}
          onClick={onUpload}
          data-testid={`reference-upload-${dataset.code}`}
        >
          {t('console:reference_data.upload.open')}
        </Button>
      </Stack>
      <DataTable
        captionKey="console:reference_data.versions_caption"
        columns={columns}
        rows={versions}
        getRowId={(row) => row.id}
        emptyTitleKey="console:reference_data.no_versions"
      />
    </Paper>
  );
}

/** صفحه مشترک داده مرجع؛ <c>only</c> فهرست را به مجموعه‌داده‌های معین محدود می‌کند (SCR-CONSOLE-041). */
function ReferenceDataScreen({ screenId, titleKey, subtitleKey, only }: { screenId: string; titleKey: string; subtitleKey: string; only?: readonly string[] }) {
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState<ReferenceDatasetView | null>(null);
  const [publishing, setPublishing] = useState<{
    dataset: ReferenceDatasetView;
    version: ReferenceVersionView;
  } | null>(null);

  const datasets = useQuery({
    queryKey: consoleKeys.referenceData,
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/reference-data')),
  });

  const publish = useMutation({
    mutationFn: ({ dataset, version }: { dataset: string; version: number }) =>
      unwrap(
        api.POST('/api/v1/platform/console/reference-data/{dataset}/versions/{version}:publish', {
          params: { path: { dataset, version } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consoleKeys.referenceData });
      setPublishing(null);
    },
  });

  return (
    <ListPage screenId={screenId} titleKey={titleKey} subtitleKey={subtitleKey}>
      <QueryState
        isPending={datasets.isPending}
        error={datasets.error}
        data={datasets.data}
        onRetry={() => void datasets.refetch()}
      >
        {(data) => (
          <Stack spacing={2}>
            {data.items
              .filter((dataset) => !only || only.includes(dataset.code))
              .map((dataset) => (
                <DatasetCard
                  key={dataset.code}
                  dataset={dataset}
                  onUpload={() => setUploading(dataset)}
                  onPublish={(version) => setPublishing({ dataset, version })}
                />
              ))}
          </Stack>
        )}
      </QueryState>
      {uploading && <UploadDialog dataset={uploading} open onClose={() => setUploading(null)} />}
      <ConfirmDialog
        open={publishing !== null}
        titleKey="console:reference_data.publish_title"
        messageKey="console:reference_data.publish_message"
        messageValues={{
          code: publishing?.dataset.code ?? '',
          version: faDigits(toNumber(publishing?.version.version) ?? 0),
        }}
        confirmKey="console:reference_data.publish"
        busy={publish.isPending}
        error={publish.error}
        onCancel={() => {
          publish.reset();
          setPublishing(null);
        }}
        onConfirm={() =>
          publishing &&
          publish.mutate({
            dataset: publishing.dataset.code,
            version: toNumber(publishing.version.version) ?? 0,
          })
        }
      />
    </ListPage>
  );
}

/** SCR-CONSOLE-008 — همه مجموعه‌داده‌های مرجع (US-PLT-090). */
export function ReferenceDataPage() {
  return (
    <ReferenceDataScreen
      screenId={SCR_CONSOLE_008}
      titleKey="console:reference_data.title"
      subtitleKey="console:reference_data.subtitle"
    />
  );
}

/** SCR-CONSOLE-041 — فهرست‌های مقرراتی (US-CFG-042): همان مکانیزم نسخه‌دار، محدود به سه فهرست مقرراتی. */
export function RegulatoryListsPage() {
  return (
    <ReferenceDataScreen
      screenId={SCR_CONSOLE_041}
      titleKey="console:regulatory_lists.title"
      subtitleKey="console:regulatory_lists.subtitle"
      only={REGULATORY_DATASETS}
    />
  );
}
